import { html, LitElement } from 'lit';
import { reset } from '@cof/refi-web-sdk/dist/styles/reset.style.js';
import { updateMicroStepEvent } from '@cof/refi-web-sdk/dist/utils/update-progress-bar.js';
import { TagKey } from '@cof/tag-catalog-fs-offers-mfe';
import { style } from './refi-iconic-offer-card.style.js';
import { gravityTooltip, gravityCard, gravityButton, gravitySpacing, gravityTypography } from '../../shared/styles/gravity.style.js';
import { ariaOnly } from '../../shared/styles/aria.style.js';
import { buttons } from '../../shared/styles/buttons.style.js';
import { REFI_OFFERS_CONST } from '../../shared/constants/app.constants.js';
import { vibrate } from '../../shared/decorators/vibrate.decorator.js';
import { OffersService } from '../offers.service.js';
import { stratum } from '../../../constants/stratum.js';
import { AppService } from '../../app.service.js';
import { HostAppEventsService } from '../../shared/services/host-app-events.service.js';

const termComparisonHeaders = {
  DEFAULT: {
    LONGER_TERM: 'Extend loan term',
    LONGEST_TERM: 'Longest loan term',
    SAME_TERM: 'Same loan term',
    SHORTER_TERM: 'Shorten loan term',
    SHORTEST_TERM: 'Shortest loan term',
  },
};
Object.freeze(termComparisonHeaders);

class RefiIconicOfferCardComponent extends LitElement {
  constructor() {
    super();
    this.adjustedLoanCalculatedRemainingTerm = OffersService.offerPageVisualExperienceResponse?.experienceProperties?.CALCULATED_TERM || '';
    this.showError = false;
    this.loading = false;
  }

  static get properties() {
    return {
      loanAction: { type: String, attribute: 'loan-action' },
      currentLoan: { type: Object, attribute: 'current-loan' },
      offer: { type: Object, attribute: true },
      termComparison: { type: String, attribute: true },
      readOnly: { type: Boolean, attribute: 'read-only' },
      showFastpath: { type: Boolean, attribute: 'show-fastpath' },
      customerDetail: { type: Object, attribute: true },
      alignCardCenter: { type: Boolean, attribute: true },
      isSingleOffer: { type: Boolean, attribute: true },
      hasSavings: { type: Boolean, attribute: true },
      hasStips: { type: Boolean, attribute: true },
      callToAction: { type: String, attribute: true },
      showError: { type: Boolean, attribute: true },
      loading: { type: Boolean },
    };
  }

  static get styles() {
    return [reset, ariaOnly, buttons, style, gravityTooltip, gravityCard, gravityButton, gravitySpacing, gravityTypography];
  }

  render() {
    return html`
      <div class="card-compare-old-loan">
        <refi-iconic-offer-header .offer="${this.offer}"></refi-iconic-offer-header>
        <refi-iconic-offer-card-details monthly-payment="${this.offer.offerPayment}" offer-apr="${this.offer.offerApr}" term="${this.offer.term}" current-loan-term="${this.currentLoan.remainingPayment}" gap-class="card-details-gap">
        </refi-iconic-offer-card-details>
        ${!this.readOnly
          ? html` <fs-button variant="primary" ?loading="${this.loading}" class="select-offer-btn _gb-show" type="button" @click="${this.selectOffer}" data-e2e-ref="offerContinue" aria-label="${this.termComparisonHeader()}, continue"
              >Continue</fs-button
            >`
          : ''}
        ${this.showError ? html` <offer-error-message></offer-error-message>` : ''}
      </div>
    `;
  }

  termComparisonHeader() {
    const termKey = `${this.offer.meta.term}_TERM`;
    return termComparisonHeaders.DEFAULT[termKey];
  }

  firstUpdated() {
    if (!this.readOnly) {
      REFI_OFFERS_CONST.TRACK_OFFER(this.offer);
    }
  }

  @vibrate(50)
  selectOffer() {
    if (this.loading) return;
    this.showError = false;
    this.loading = true;
    OffersService.setSelectedOffer(this.offer)
      .then(() => {
        stratum.publishTag(TagKey.OFFERS_SELECTED_OFFER, {
          replacements: {
            value: `term:${this.offer.term} apr:${this.offer.offerApr} amt:${this.offer.offerPayment}`,
          },
        });
        updateMicroStepEvent('Offers');
        if (AppService.isV2Routing) {
          HostAppEventsService.sendNextMfeEvent();
        } else {
          this.dispatchEvent(
            new CustomEvent('host-app-event', {
              bubbles: true,
              composed: true,
              detail: {
                actionName: 'next-mfe',
                data: {
                  currentMfe: 'offers',
                },
              },
            }),
          );
        }
        this.loading = false;
      })
      .catch(() => {
        this.showError = true;
        this.loading = false;
      });
  }
}

customElements.define('refi-iconic-offer-card', RefiIconicOfferCardComponent);
